import { NotificationService } from '@shared/feature/notification-handling';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TableData } from '@shared/ui/tables';
import { catchError, exhaustMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { BaseListOfficerService } from '../api/base-list-officer.service';
import { Officer, OfficerBase, OfficerResponse } from '../models/list-officer.models';
import * as ListOfficerActions from './list-officer-store.actions';
import { ActionType, ListOfficerActionResult } from '../models/list-officer-action-result.models';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { TranslationFromJsonService } from '@shared/feature/translate';
import { CacheService, CacheUsersInitiator } from '@shared/utils/cache';

@Injectable()
export class ListOfficerEffects {
  listOfficer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListOfficerActions.listOfficer),
      switchMap(() =>
        this.service.list('').pipe(
          map(
            (response) =>
              new TableData(
                response.value.count,
                response.value.data.map((x) => this.transformOfficerResponse(x))
              )
          ),
          map((response) => ListOfficerActions.listOfficerSuccess({ officers: response.data }))
        )
      )
    )
  );

  listPaginationOfficers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListOfficerActions.listPaginationOfficers),
      switchMap((request) =>
        this.service.list(request.query).pipe(
          map(
            (response) =>
              new TableData(
                response.value.count,
                response.value.data.map((x) => this.transformOfficerResponse(x))
              )
          ),
          map((response) =>
            ListOfficerActions.listPaginationOfficersSuccess({ paginationOfficers: response.data, query: request.query, filteredOfficersTotalCount: response.count })
          )
        )
      )
    )
  );

  deleteOfficer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ListOfficerActions.deleteOfficer),
      exhaustMap((action) => {
        return this.service.deleteOfficer(action.uid).pipe(
          tap(() => {
            this.notificationService.addSuccess(this.translationFromJsonService.instant('user-is-deleted'));
          }),
          map(() => {
            return ListOfficerActions.deleteOfficerSuccess({
              uid: action.uid,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(ListOfficerActions.listOfficerActionResult({ actionResult: new ListOfficerActionResult(ActionType.DeleteOfficer, error, false) }))
          )
        );
      })
    );
  });

  deleteOfficerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ListOfficerActions.deleteOfficerSuccess),
        tap(() => {
          this.cacheService.usersDataModified(CacheUsersInitiator.UserDeleted);
        })
      ),
    { dispatch: false }
  );

  inviteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListOfficerActions.inviteUser),
      mergeMap((action) =>
        this.service.inviteUser(action.uid).pipe(
          tap(() => {
            this.notificationService.addSuccess(this.translationFromJsonService.instant('user-is-invited'));
          }),
          map(() => ListOfficerActions.inviteUserSuccess({ uid: action.uid })),
          catchError((error: HttpErrorResponse) => of(ListOfficerActions.inviteUserFail({ uid: action.uid, error })))
        )
      )
    )
  );

  inviteUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ListOfficerActions.inviteUserSuccess),
        tap(() => {
          this.cacheService.usersDataModified(CacheUsersInitiator.UserInvited);
        })
      ),
    { dispatch: false }
  );

  inviteUserFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ListOfficerActions.inviteUserFail),
        tap((action) => {
          const message = typeof action.error === 'string' ? action.error : this.translationFromJsonService.instant('user-invite-failed');
          this.notificationService.addError(message);
        })
      ),
    { dispatch: false }
  );

  listOfficerForOrganizationChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListOfficerActions.listOfficerForOrganizationChart),
      switchMap(() =>
        this.service.listOrgChartOfficers().pipe(
          map((result) => ListOfficerActions.listOfficerForOrganizationChartSuccess({ orgChartOfficers: result.value })),
          catchError((error) => of(ListOfficerActions.listOfficerForOrganizationChartFail({ error })))
        )
      )
    )
  );

  listOfficerForOrganizationChartFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ListOfficerActions.listOfficerForOrganizationChartFail),
        tap((action) => {
          const message = typeof action.error === 'string' ? action.error : this.translationFromJsonService.instant('organization-chart-data-load-failed');
          this.notificationService.addError(message);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private service: BaseListOfficerService,
    private notificationService: NotificationService,
    private translationFromJsonService: TranslationFromJsonService,
    private cacheService: CacheService
  ) {}

  private transformOfficerResponse(x: OfficerResponse): Officer {
    return new Officer(
      x.uid,
      x.firstName,
      x.lastName,
      x.deletedOn !== null,
      x.emailAddress,
      x.timezone,
      x.employeeID,
      x.pictureFullPath,
      x.accountType,
      x.inviteSent,
      x.phone,
      x.role,
      x.shareType,
      x.respondsTo
        ? new OfficerBase(
            x.respondsTo.uid,
            x.respondsTo.firstName,
            x.respondsTo.lastName,
            x.respondsTo.deletedOn !== null,
            x.respondsTo.emailAddress,
            x.respondsTo.timezone,
            x.respondsTo.timezone,
            x.respondsTo.pictureFullPath
          )
        : null,
      x.children,
      false
    );
  }
}
